import {
  normalizeProductType,
  recommendFit,
  resolveFitPreferences,
} from '@theme/modaest-fit-recommend';

const STORAGE_KEY = 'modaest.fitProfile.v1';

/**
 * QA theme bridge: merge approved policy patch into shop metafield config.
 * Shop metafield values win when already set; patch fills empty overrides / missing recommendation keys.
 * @param {object | null | undefined} shopConfig
 * @param {Element | null} patchScript
 * @returns {object | null | undefined}
 */
function mergeShopConfigPatch(shopConfig, patchScript) {
  if (!shopConfig || typeof shopConfig !== 'object') return shopConfig;
  if (!(patchScript instanceof HTMLScriptElement)) return shopConfig;
  let patch;
  try {
    patch = JSON.parse(patchScript.textContent || '{}');
  } catch {
    return shopConfig;
  }
  if (!patch || typeof patch !== 'object') return shopConfig;

  const next = { ...shopConfig };
  const existingOverrides =
    shopConfig.productTypeOverrides && typeof shopConfig.productTypeOverrides === 'object'
      ? shopConfig.productTypeOverrides
      : {};
  const patchOverrides =
    patch.productTypeOverrides && typeof patch.productTypeOverrides === 'object'
      ? patch.productTypeOverrides
      : {};
  next.productTypeOverrides = { ...patchOverrides, ...existingOverrides };

  const existingRec =
    shopConfig.recommendation && typeof shopConfig.recommendation === 'object'
      ? shopConfig.recommendation
      : {};
  const patchRec =
    patch.recommendation && typeof patch.recommendation === 'object' ? patch.recommendation : {};
  next.recommendation = {
    ...patchRec,
    ...existingRec,
    copy: {
      ...(patchRec.copy && typeof patchRec.copy === 'object' ? patchRec.copy : {}),
      ...(existingRec.copy && typeof existingRec.copy === 'object' ? existingRec.copy : {}),
    },
  };

  // QA-only bodyModel overlays (e.g. weight slider max) without replacing the shop grid unless provided.
  if (patch.bodyModel && typeof patch.bodyModel === 'object') {
    const existingModel =
      shopConfig.bodyModel && typeof shopConfig.bodyModel === 'object' ? shopConfig.bodyModel : {};
    const patchInputs =
      patch.bodyModel.inputs && typeof patch.bodyModel.inputs === 'object' ? patch.bodyModel.inputs : {};
    const existingInputs =
      existingModel.inputs && typeof existingModel.inputs === 'object' ? existingModel.inputs : {};
    next.bodyModel = {
      ...existingModel,
      ...patch.bodyModel,
      inputs: {
        ...existingInputs,
        ...patchInputs,
        heightCm: {
          ...(existingInputs.heightCm && typeof existingInputs.heightCm === 'object'
            ? existingInputs.heightCm
            : {}),
          ...(patchInputs.heightCm && typeof patchInputs.heightCm === 'object'
            ? patchInputs.heightCm
            : {}),
        },
        weightKg: {
          ...(existingInputs.weightKg && typeof existingInputs.weightKg === 'object'
            ? existingInputs.weightKg
            : {}),
          ...(patchInputs.weightKg && typeof patchInputs.weightKg === 'object'
            ? patchInputs.weightKg
            : {}),
        },
      },
      targets: patch.bodyModel.targets || existingModel.targets,
    };
  }
  return next;
}

/**
 * Visualizer-only helpers. Never used by recommendFit / Brand Fit Map.
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
function clampNumber(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/**
 * @param {number} a
 * @param {number} b
 * @param {number} t
 */
function lerpNumber(a, b, t) {
  return a + (b - a) * t;
}

/**
 * Soft ease for weight fill so extremes stay natural.
 * Blend keeps low weights slim while mid/high fill earlier (helps ~118 kg read fuller).
 * @param {number} t 0–1
 */
function easeWeight(t) {
  const x = clampNumber(t, 0, 1);
  const smooth = x * x * (3 - 2 * x);
  return x * 0.28 + smooth * 0.72;
}

/**
 * Product-level body presentation mode from productFit.presentation.
 * "unisex" (or missing/invalid) → show Male/Female/Prefer-not-to-specify selector.
 * "male" | "female" → lock presentation and hide selector.
 * @param {object | null | undefined} productFit
 * @returns {'male' | 'female' | 'unisex'}
 */
function resolveProductPresentationMode(productFit) {
  const raw = typeof productFit?.presentation === 'string' ? productFit.presentation.trim().toLowerCase() : '';
  if (raw === 'male' || raw === 'female' || raw === 'unisex') return raw;
  return 'unisex';
}

/**
 * Keep only productFit.fields the shop body model can estimate.
 * Chart-only columns (e.g. length/waist) may be stored on the product for the
 * size chart but use bodyTargets the Brand Fit Map does not model yet. Passing
 * those through as required fields makes recommendFit return `insufficient`
 * even when presentation/height/weight are valid — without changing formulas.
 * @param {object | null | undefined} productFit
 * @param {object | null | undefined} shopConfig
 * @returns {object | null | undefined}
 */
function productFitForRecommendation(productFit, shopConfig) {
  if (!productFit || typeof productFit !== 'object') return productFit;
  const targets = shopConfig?.bodyModel?.targets;
  if (!targets || typeof targets !== 'object') return productFit;
  const sourceFields = productFit.fields;
  if (!sourceFields || typeof sourceFields !== 'object') return productFit;

  /** @type {Record<string, object>} */
  const fields = {};
  for (const [key, field] of Object.entries(sourceFields)) {
    if (!field || typeof field !== 'object') continue;
    const bodyTarget = typeof field.bodyTarget === 'string' ? field.bodyTarget : '';
    if (bodyTarget && Object.prototype.hasOwnProperty.call(targets, bodyTarget)) {
      fields[key] = field;
    }
  }
  if (Object.keys(fields).length === 0) return productFit;
  if (Object.keys(fields).length === Object.keys(sourceFields).length) return productFit;
  return { ...productFit, fields };
}

/**
 * Build a front-facing anatomical body outline in a 100×200 viewBox.
 * Visualizer-only. Feet stay on the baseline; height grows upward;
 * weight widens torso / waist / hip / arm / thigh / calf — not uniform X-scale.
 * @param {{ heightCm: number, weightKg: number, presentation: string, heightMin: number, heightMax: number, weightMin: number, weightMax: number }} opts
 * @returns {string}
 */
function buildBodySilhouettePath(opts) {
  const heightSpan = Math.max(1, opts.heightMax - opts.heightMin);
  const weightSpan = Math.max(1, opts.weightMax - opts.weightMin);
  const ht = clampNumber((opts.heightCm - opts.heightMin) / heightSpan, 0, 1);
  const wt = easeWeight((opts.weightKg - opts.weightMin) / weightSpan);

  const presentation =
    opts.presentation === 'male' || opts.presentation === 'female' ? opts.presentation : 'unspecified';

  /** @type {Record<string, { shoulder: number, chest: number, waist: number, hip: number, arm: number, forearm: number, thigh: number, calf: number, head: number, neck: number }>} */
  const bases = {
    male: {
      shoulder: 26.5,
      chest: 23.0,
      waist: 16.0,
      hip: 19.5,
      arm: 7.2,
      forearm: 5.6,
      thigh: 10.2,
      calf: 6.4,
      head: 9.4,
      neck: 4.5,
    },
    female: {
      shoulder: 22.0,
      chest: 19.6,
      waist: 12.6,
      hip: 23.6,
      arm: 6.2,
      forearm: 4.8,
      thigh: 9.8,
      calf: 5.8,
      head: 9.0,
      neck: 3.6,
    },
    unspecified: {
      shoulder: 24.2,
      chest: 21.2,
      waist: 14.4,
      hip: 21.4,
      arm: 6.7,
      forearm: 5.2,
      thigh: 10.0,
      calf: 6.1,
      head: 9.2,
      neck: 4.0,
    },
  };
  const base = bases[presentation];

  const shoulder = base.shoulder * lerpNumber(0.86, 1.34, wt);
  const chest = base.chest * lerpNumber(0.84, 1.46, wt);
  const waist = base.waist * lerpNumber(0.8, 1.62, wt);
  const hip = base.hip * lerpNumber(0.88, 1.48, wt);
  const arm = base.arm * lerpNumber(0.86, 1.48, wt);
  const forearm = base.forearm * lerpNumber(0.88, 1.4, wt);
  const thigh = base.thigh * lerpNumber(0.86, 1.52, wt);
  const calf = base.calf * lerpNumber(0.9, 1.36, wt);
  const headRx = base.head * lerpNumber(0.97, 1.06, wt);
  const headRy = base.head * 1.12 * lerpNumber(0.98, 1.04, wt);
  const neck = base.neck * lerpNumber(0.94, 1.14, wt);

  const cx = 50;
  const baseline = 196;
  const figureH = lerpNumber(152, 190, ht);
  const top = baseline - figureH;

  const yHead = top + headRy;
  const yNeck = yHead + headRy * 0.68;
  const yShoulder = yNeck + figureH * 0.028;
  const yChest = yShoulder + figureH * 0.1;
  const yArmpit = yShoulder + figureH * 0.13;
  const yElbow = yShoulder + figureH * 0.28;
  const yWaist = yShoulder + figureH * 0.3;
  const yHip = yShoulder + figureH * 0.42;
  const yCrotch = yHip + figureH * 0.048;
  const yThigh = yCrotch + (baseline - yCrotch) * 0.28;
  const yKnee = yCrotch + (baseline - yCrotch) * 0.5;
  const yCalf = yCrotch + (baseline - yCrotch) * 0.72;
  const yAnkle = baseline - 5;
  const yHand = Math.min(baseline - 20, yElbow + figureH * 0.12);

  // Arms hang close to the torso with real thickness (Maison Block-style).
  const outerShoulder = shoulder + arm * 0.2;
  const outerElbow = chest + arm * 1.05;
  const outerHand = chest + arm * 0.7 + forearm * 0.55;
  const innerHand = outerHand - forearm * 0.85;
  const innerElbow = chest + arm * 0.15;

  const p = (x, y) => `${(+x).toFixed(2)} ${(+y).toFixed(2)}`;
  const L = (x, y) => `L ${p(x, y)}`;
  const Q = (cx1, cy1, x, y) => `Q ${p(cx1, cy1)} ${p(x, y)}`;

  return [
    `M ${p(cx - 5.8, baseline)}`,
    L(cx - 7.2, yAnkle),
    Q(cx - calf, yCalf, cx - calf * 1.02, yKnee),
    Q(cx - thigh * 0.98, yThigh, cx - thigh, yCrotch),
    Q(cx - hip * 1.02, (yCrotch + yHip) / 2, cx - hip, yHip),
    Q(cx - (hip * 0.55 + waist * 0.45), (yHip + yWaist) / 2, cx - waist, yWaist),
    Q(cx - (waist * 0.4 + chest * 0.6), (yWaist + yChest) / 2, cx - chest, yChest),
    L(cx - chest * 0.98, yArmpit),
    L(cx - outerShoulder, yShoulder + 1.5),
    Q(cx - outerElbow, yShoulder + (yElbow - yShoulder) * 0.45, cx - outerElbow, yElbow),
    Q(cx - outerHand, yElbow + (yHand - yElbow) * 0.55, cx - outerHand, yHand),
    L(cx - innerHand, yHand + 2.2),
    Q(cx - innerElbow, yElbow + (yHand - yElbow) * 0.4, cx - innerElbow, yElbow),
    Q(cx - chest * 0.92, yArmpit + 4, cx - chest * 0.9, yArmpit),
    L(cx - shoulder * 0.92, yShoulder),
    L(cx - neck, yNeck),
    `A ${headRx.toFixed(2)} ${headRy.toFixed(2)} 0 1 1 ${p(cx + neck, yNeck)}`,
    L(cx + shoulder * 0.92, yShoulder),
    L(cx + chest * 0.9, yArmpit),
    Q(cx + chest * 0.92, yArmpit + 4, cx + innerElbow, yElbow),
    Q(cx + innerElbow, yElbow + (yHand - yElbow) * 0.4, cx + innerHand, yHand + 2.2),
    L(cx + outerHand, yHand),
    Q(cx + outerHand, yElbow + (yHand - yElbow) * 0.55, cx + outerElbow, yElbow),
    Q(cx + outerElbow, yShoulder + (yElbow - yShoulder) * 0.45, cx + outerShoulder, yShoulder + 1.5),
    L(cx + chest * 0.98, yArmpit),
    L(cx + chest, yChest),
    Q(cx + (waist * 0.4 + chest * 0.6), (yWaist + yChest) / 2, cx + waist, yWaist),
    Q(cx + (hip * 0.55 + waist * 0.45), (yHip + yWaist) / 2, cx + hip, yHip),
    Q(cx + hip * 1.02, (yCrotch + yHip) / 2, cx + thigh, yCrotch),
    Q(cx + thigh * 0.98, yThigh, cx + calf * 1.02, yKnee),
    Q(cx + calf, yCalf, cx + 7.2, yAnkle),
    L(cx + 5.8, baseline),
    'Z',
  ].join(' ');
}

/**
 * @returns {{ version: number, body: object, fitByGroup: Record<string, string> }}
 */
function emptyProfile() {
  return {
    version: 1,
    body: {
      presentation: null,
      heightCm: null,
      weightKg: null,
    },
    fitByGroup: {},
  };
}

/**
 * @returns {{ version: number, body: object, fitByGroup: Record<string, string> }}
 */
function readStoredProfile() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyProfile();
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== 1) return emptyProfile();
    return {
      version: 1,
      body: {
        presentation: parsed.body?.presentation ?? null,
        heightCm: typeof parsed.body?.heightCm === 'number' ? parsed.body.heightCm : null,
        weightKg: typeof parsed.body?.weightKg === 'number' ? parsed.body.weightKg : null,
      },
      fitByGroup:
        parsed.fitByGroup && typeof parsed.fitByGroup === 'object' ? { ...parsed.fitByGroup } : {},
    };
  } catch {
    return emptyProfile();
  }
}

/**
 * @param {{ version: number, body: object, fitByGroup: Record<string, string> }} profile
 */
function writeStoredProfile(profile) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        version: 1,
        body: {
          presentation: profile.body.presentation,
          heightCm: profile.body.heightCm,
          weightKg: profile.body.weightKg,
        },
        fitByGroup: { ...profile.fitByGroup },
      })
    );
  } catch {
    /* private mode / quota */
  }
}

class ModaestFindYourFit extends HTMLElement {
  /** @type {object | null} */
  #config = null;
  /** @type {ReturnType<typeof emptyProfile>} */
  #profile = emptyProfile();
  /** @type {string | null} */
  #groupKey = null;
  /** @type {object[]} */
  #preferences = [];
  /** @type {'male' | 'female' | 'unisex'} */
  #presentationMode = 'unisex';

  connectedCallback() {
    const script = this.querySelector('script[type="application/json"][data-modaest-fit-payload]');
    if (!(script instanceof HTMLScriptElement)) return;
    try {
      this.#config = JSON.parse(script.textContent || '{}');
    } catch {
      this.#config = null;
      return;
    }

    this.#config.shopConfig = mergeShopConfigPatch(
      this.#config.shopConfig,
      this.querySelector('script[type="application/json"][data-modaest-fit-config-patch]')
    );

    this.#groupKey = normalizeProductType(this.#config?.productType);
    this.#preferences = resolveFitPreferences(
      this.#config?.shopConfig,
      this.#config?.productFit,
      this.#config?.productType
    );
    this.#presentationMode = resolveProductPresentationMode(this.#config?.productFit);
    this.#profile = readStoredProfile();
    // Product-locked presentation wins over any stored selector choice.
    if (this.#presentationMode === 'male' || this.#presentationMode === 'female') {
      this.#profile.body.presentation = this.#presentationMode;
    }
    this.#bind();
    this.#renderPreferenceOptions();
    this.#hydrateForm();
    // Sync DOM defaults / locked presentation into profile before first recommend.
    this.#captureForm();
    this.#render();
  }

  #bind() {
    this.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-modaest-fit-open]')) {
        const dialog = this.querySelector('dialog');
        if (dialog instanceof HTMLDialogElement) dialog.showModal();
      }
      if (target.closest('[data-modaest-fit-close]')) {
        const dialog = this.querySelector('dialog');
        if (dialog instanceof HTMLDialogElement) dialog.close();
      }
      if (target.closest('[data-modaest-fit-apply]')) {
        this.#applySize();
      }
    });

    this.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (
        target.matches(
          '[data-modaest-fit-presentation], [data-modaest-fit-height], [data-modaest-fit-weight], [data-modaest-fit-preference]'
        )
      ) {
        this.#captureForm();
        this.#persistAllowed();
        this.#updateSilhouette();
        this.#renderResult();
      }
    });

    this.addEventListener('input', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.matches('[data-modaest-fit-height], [data-modaest-fit-weight]')) {
        this.#captureForm();
        this.#updateRangeLabels();
        this.#persistAllowed();
        this.#renderResult();
      }
    });
  }

  #defaults() {
    const model = this.#config?.shopConfig?.bodyModel;
    return {
      heightMin: model?.inputs?.heightCm?.min ?? 122,
      heightMax: model?.inputs?.heightCm?.max ?? 226,
      heightDefault: model?.inputs?.heightCm?.default ?? 152,
      weightMin: model?.inputs?.weightKg?.min ?? 40,
      weightMax: Math.max(model?.inputs?.weightKg?.max ?? 125, 125),
      weightDefault: model?.inputs?.weightKg?.default ?? 50,
    };
  }

  #renderPreferenceOptions() {
    const host = this.querySelector('[data-modaest-fit-preferences-host]');
    if (!(host instanceof HTMLElement)) return;
    if (this.#preferences.length === 0) {
      host.innerHTML = '';
      return;
    }

    const name = `modaest-fit-preference-${this.id || 'fyf'}`;
    const options = this.#preferences
      .map(
        (preference) => `
      <label class="modaest-fit__choice">
        <input
          type="radio"
          name="${escapeHtml(name)}"
          value="${escapeHtml(preference.id)}"
          data-modaest-fit-preference
        >
        ${escapeHtml(preference.label)}
      </label>`
      )
      .join('');

    host.innerHTML = `
      <fieldset class="modaest-fit__fieldset">
        <legend class="modaest-fit__legend">Preferred fit</legend>
        <div class="modaest-fit__choices">${options}</div>
      </fieldset>
    `;
  }

  #hydrateForm() {
    const d = this.#defaults();
    if (this.#presentationMode === 'male' || this.#presentationMode === 'female') {
      this.#profile.body.presentation = this.#presentationMode;
    } else {
      const presentation = this.#profile.body.presentation;
      if (presentation) {
        const radio = this.querySelector(
          `[data-modaest-fit-presentation][value="${CSS.escape(presentation)}"]`
        );
        if (radio instanceof HTMLInputElement) radio.checked = true;
      }
    }

    const height = this.querySelector('[data-modaest-fit-height]');
    const weight = this.querySelector('[data-modaest-fit-weight]');
    if (height instanceof HTMLInputElement) {
      height.min = String(d.heightMin);
      height.max = String(d.heightMax);
      height.value = String(
        typeof this.#profile.body.heightCm === 'number' ? this.#profile.body.heightCm : d.heightDefault
      );
    }
    if (weight instanceof HTMLInputElement) {
      weight.min = String(d.weightMin);
      weight.max = String(d.weightMax);
      weight.value = String(
        typeof this.#profile.body.weightKg === 'number' ? this.#profile.body.weightKg : d.weightDefault
      );
    }

    if (this.#preferences.length > 0 && this.#groupKey) {
      const savedId = this.#profile.fitByGroup[this.#groupKey];
      if (savedId && this.#preferences.some((p) => p.id === savedId)) {
        const prefRadio = this.querySelector(
          `[data-modaest-fit-preference][value="${CSS.escape(savedId)}"]`
        );
        if (prefRadio instanceof HTMLInputElement) prefRadio.checked = true;
      }
    }

    this.#updateRangeLabels();
  }

  #captureForm() {
    const presentationEl = this.querySelector('[data-modaest-fit-presentation]:checked');
    const heightEl = this.querySelector('[data-modaest-fit-height]');
    const weightEl = this.querySelector('[data-modaest-fit-weight]');
    const prefEl = this.querySelector('[data-modaest-fit-preference]:checked');

    if (this.#presentationMode === 'male' || this.#presentationMode === 'female') {
      this.#profile.body.presentation = this.#presentationMode;
    } else {
      this.#profile.body.presentation =
        presentationEl instanceof HTMLInputElement ? presentationEl.value : null;
    }
    this.#profile.body.heightCm =
      heightEl instanceof HTMLInputElement ? Number(heightEl.value) : null;
    this.#profile.body.weightKg =
      weightEl instanceof HTMLInputElement ? Number(weightEl.value) : null;

    if (this.#preferences.length > 0 && this.#groupKey && prefEl instanceof HTMLInputElement) {
      const id = prefEl.value;
      if (this.#preferences.some((p) => p.id === id)) {
        this.#profile.fitByGroup[this.#groupKey] = id;
      }
    }
  }

  /**
   * Active body presentation for recommend + silhouette.
   * Locked product modes always win; otherwise radio / profile.
   * @returns {'male' | 'female' | 'unspecified'}
   */
  #resolvedPresentation() {
    if (this.#presentationMode === 'male' || this.#presentationMode === 'female') {
      return this.#presentationMode;
    }
    const presentationEl = this.querySelector('[data-modaest-fit-presentation]:checked');
    if (presentationEl instanceof HTMLInputElement) {
      const value = presentationEl.value;
      if (value === 'male' || value === 'female' || value === 'unspecified') return value;
    }
    const stored = this.#profile.body.presentation;
    if (stored === 'male' || stored === 'female' || stored === 'unspecified') return stored;
    return 'unspecified';
  }

  #persistAllowed() {
    if (!this.#groupKey) return;
    writeStoredProfile(this.#profile);
  }

  #currentFitPreferenceId() {
    if (this.#preferences.length === 0) return null;
    const prefEl = this.querySelector('[data-modaest-fit-preference]:checked');
    if (!(prefEl instanceof HTMLInputElement)) return null;
    const id = prefEl.value;
    return this.#preferences.some((p) => p.id === id) ? id : null;
  }

  #selectedNonSizeOptions() {
    /** @type {Record<string, string>} */
    const selected = {};
    const optionName = this.#config?.productFit?.optionName || 'Size';
    document.querySelectorAll('variant-picker input[type="radio"][data-option-name]:checked').forEach((el) => {
      if (!(el instanceof HTMLInputElement)) return;
      const name = el.getAttribute('data-option-name');
      if (!name || name === optionName) return;
      selected[name] = el.value;
    });
    return selected;
  }

  #runRecommend() {
    if (!this.#groupKey) {
      return { status: 'no_data', fallback: 'size_chart' };
    }
    if (!this.#config?.productFit) {
      return { status: 'no_data', fallback: 'size_chart' };
    }
    if (!this.#config?.shopConfig) {
      return { status: 'no_data', fallback: 'size_chart' };
    }

    const presentation = this.#profile.body.presentation;
    if (presentation !== 'male' && presentation !== 'female' && presentation !== 'unspecified') {
      return { status: 'insufficient', fallback: 'size_chart' };
    }

    const fitPreferenceId = this.#currentFitPreferenceId();
    if (this.#preferences.length > 0 && !fitPreferenceId) {
      return { status: 'no_preference', fallback: 'size_chart' };
    }

    return recommendFit({
      profile: {
        presentation,
        heightCm: this.#profile.body.heightCm,
        weightKg: this.#profile.body.weightKg,
      },
      fitPreferenceId,
      productFit: productFitForRecommendation(this.#config.productFit, this.#config.shopConfig),
      shopConfig: this.#config.shopConfig,
      productType: this.#config.productType,
      selectedOptions: this.#selectedNonSizeOptions(),
      variants: Array.isArray(this.#config.variants) ? this.#config.variants : [],
    });
  }

  #statusMessage(result) {
    switch (result.status) {
      case 'ok':
        return null;
      case 'no_data':
        return 'Find Your Fit is not available for this product. Use the size chart.';
      case 'no_model':
        return 'Size estimation is not configured yet. Use the size chart for guidance.';
      case 'out_of_range':
        return 'Height or weight is outside the supported range. Adjust your profile or use the size chart.';
      case 'no_preference':
        return 'Select a fit preference to see an estimated size.';
      case 'invalid_preference':
        return 'This fit preference is missing valid ease settings. Use the size chart.';
      case 'missing_ease':
        return 'Fit ease is not configured for this product. Use the size chart.';
      case 'invalid_ease':
        return 'Product fit ease is invalid. Use the size chart.';
      case 'insufficient':
        return 'Add your presentation, height, and weight to see an estimated size.';
      case 'no_match':
        return 'No available size matched your profile. Check the size chart.';
      case 'unsupported_version':
        return 'Find Your Fit data needs an update. Use the size chart.';
      default:
        return 'Use the size chart for guidance.';
    }
  }

  #updateRangeLabels() {
    const heightEl = this.querySelector('[data-modaest-fit-height]');
    const weightEl = this.querySelector('[data-modaest-fit-weight]');
    const heightOut = this.querySelector('[data-modaest-fit-height-label]');
    const weightOut = this.querySelector('[data-modaest-fit-weight-label]');
    if (heightEl instanceof HTMLInputElement && heightOut) {
      const cm = Math.round(Number(heightEl.value));
      const totalIn = cm / 2.54;
      let feet = Math.floor(totalIn / 12);
      let inches = Math.round(totalIn - feet * 12);
      if (inches === 12) {
        feet += 1;
        inches = 0;
      }
      heightOut.textContent = `${cm} cm · ${feet}'${inches}"`;
      this.#syncSliderFill(heightEl);
    }
    if (weightEl instanceof HTMLInputElement && weightOut) {
      weightOut.textContent = `${Number(weightEl.value)} kg`;
      this.#syncSliderFill(weightEl);
    }
    this.#updateSilhouette();
  }

  /**
   * Visual-only: drive the filled track percentage for styled range inputs.
   * @param {HTMLInputElement} el
   */
  #syncSliderFill(el) {
    const min = Number(el.min);
    const max = Number(el.max);
    const value = Number(el.value);
    const span = max - min;
    const pct = span > 0 ? ((value - min) / span) * 100 : 0;
    el.style.setProperty('--modaest-fit-fill', `${Math.min(100, Math.max(0, pct))}%`);
  }

  /**
   * Visual-only body silhouette. Does not feed recommendFit or variant selection.
   */
  #updateSilhouette() {
    const pathEl = this.querySelector('[data-modaest-fit-silhouette-path]');
    if (!(pathEl instanceof SVGPathElement)) return;

    const heightEl = this.querySelector('[data-modaest-fit-height]');
    const weightEl = this.querySelector('[data-modaest-fit-weight]');
    const d = this.#defaults();

    const heightCm =
      heightEl instanceof HTMLInputElement ? Number(heightEl.value) : d.heightDefault;
    const weightKg =
      weightEl instanceof HTMLInputElement ? Number(weightEl.value) : d.weightDefault;
    const presentation = this.#resolvedPresentation();

    pathEl.setAttribute(
      'd',
      buildBodySilhouettePath({
        heightCm,
        weightKg,
        presentation,
        heightMin: d.heightMin,
        heightMax: d.heightMax,
        weightMin: d.weightMin,
        weightMax: d.weightMax,
      })
    );
  }

  #render() {
    this.#updateSilhouette();
    this.#renderResult();
  }

  #renderResult() {
    const resultEl = this.querySelector('[data-modaest-fit-result]');
    const applyBtn = this.querySelector('[data-modaest-fit-apply]');
    const panel = this.querySelector('.modaest-fit__panel');
    if (!(resultEl instanceof HTMLElement)) return;

    const result = this.#runRecommend();
    this.dataset.lastStatus = result.status;

    if (result.status === 'ok') {
      const sizeLabel = result.sizeOptionValue || '';
      const prefId = this.#currentFitPreferenceId();
      const fitNote =
        prefId === 'oversized'
          ? '<p class="modaest-fit__result-note">Designed for an oversized fit.</p>'
          : '';
      // Customer-facing confidence label is always shown for every presentation.
      const confidenceLabel = 'Broader brand estimate';
      // Fixed guidance whenever a size is recommended (UI copy only).
      const guidanceNote = "We've recommended your size. Check the size chart to be sure.";

      resultEl.className = 'modaest-fit__result modaest-fit__result--ok';
      resultEl.innerHTML = `
        <p class="modaest-fit__result-label">Your recommended size</p>
        <p class="modaest-fit__result-size" aria-live="polite" aria-label="Size ${escapeHtml(sizeLabel)}">${escapeHtml(sizeLabel)}</p>
        ${fitNote}
        <p class="modaest-fit__result-confidence">${escapeHtml(confidenceLabel)}</p>
        <p class="modaest-fit__result-guidance">${escapeHtml(guidanceNote)}</p>
      `;
      if (panel instanceof HTMLElement) panel.classList.add('modaest-fit__panel--has-result');
      if (applyBtn instanceof HTMLButtonElement) {
        applyBtn.hidden = !result.available;
        applyBtn.dataset.size = sizeLabel;
      }
      return;
    }

    if (panel instanceof HTMLElement) panel.classList.remove('modaest-fit__panel--has-result');
    const message = this.#statusMessage(result);
    resultEl.className = 'modaest-fit__result modaest-fit__result--fallback';
    resultEl.innerHTML = message
      ? `<p class="modaest-fit__fallback">${escapeHtml(message)}</p>`
      : '';
    if (applyBtn instanceof HTMLButtonElement) {
      applyBtn.hidden = true;
      delete applyBtn.dataset.size;
    }
  }

  #applySize() {
    const result = this.#runRecommend();
    if (result.status !== 'ok' || !result.available || !result.sizeOptionValue) return;
    const optionName = this.#config?.productFit?.optionName || 'Size';
    const picker = document.querySelector('variant-picker');
    if (!(picker instanceof HTMLElement)) return;
    const input = picker.querySelector(
      `input[type="radio"][data-option-name="${CSS.escape(optionName)}"][value="${CSS.escape(result.sizeOptionValue)}"]`
    );
    if (!(input instanceof HTMLInputElement)) return;
    if (input.getAttribute('data-option-available') === 'false') return;
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    input.dispatchEvent(new Event('input', { bubbles: true }));
    // Keep Size across PDP navigations (modaest-size-memory.js).
    try {
      sessionStorage.setItem('modaest.lastSize.v1', result.sizeOptionValue);
    } catch {
      // ignore
    }
    const dialog = this.closest('dialog') || this.querySelector('dialog');
    if (dialog instanceof HTMLDialogElement) dialog.close();
  }
}

/**
 * @param {string} value
 */
function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

if (!customElements.get('modaest-find-your-fit')) {
  customElements.define('modaest-find-your-fit', ModaestFindYourFit);
}
